import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Thumbs, FreeMode } from 'swiper/modules';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';
import 'swiper/css/free-mode';
import "../../App.css";
import "./Product.css";
import API_BASE_URL from "../../api";

function Product({ isAdminLoggedIn }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct]         = useState(null);
  const [categoriesMap, setCategoriesMap] = useState({});
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const lightboxSwiperRef = useRef(null);

  const [enquiryOpen, setEnquiryOpen]   = useState(false);
  const [enqName, setEnqName]           = useState("");
  const [enqEmail, setEnqEmail]         = useState("");
  const [enqPhone, setEnqPhone]         = useState("");
  const [enqPostcode, setEnqPostcode]   = useState("");
  const [enqMessage, setEnqMessage]     = useState("");
  const [enqPrivacy, setEnqPrivacy]     = useState(false);
  const [enqLoading, setEnqLoading]     = useState(false);
  const [enqSuccess, setEnqSuccess]     = useState("");
  const [enqError, setEnqError]         = useState("");

  const { executeRecaptcha } = useGoogleReCaptcha();

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/products/${id}`);
        setProduct(response.data);
      } catch (error) {
        console.error("Error fetching product:", error);
      }
    };
    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/categories`);
        const map = {};
        (res.data || []).forEach((c) => (map[c.id] = c));
        setCategoriesMap(map);
      } catch (err) { console.error(err); }
    };
    fetchProduct();
    fetchCategories();
  }, [id]);

  // Build breadcrumb chain from category_id up to root
  const breadcrumbs = (() => {
    if (!product?.category_id || Object.keys(categoriesMap).length === 0) return [];
    const chain = [];
    let cur = categoriesMap[product.category_id];
    while (cur) {
      chain.unshift({ id: cur.id, name: cur.name });
      cur = cur.parent_id ? categoriesMap[cur.parent_id] : null;
    }
    return chain;
  })();

  const isFilled = (v) =>
      v !== null &&
      v !== undefined &&
      String(v).trim() !== "" &&
      String(v).toLowerCase() !== "null";

  const formatMoney = (value) => {
    if (!isFilled(value)) return "";
    const n = Number(value);
    if (Number.isNaN(n)) return String(value);
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format(n);
  };

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => setLightboxOpen(false);

  const openEnquiry = () => {
    setEnqSuccess("");
    setEnqError("");
    setEnquiryOpen(true);
  };

  const closeEnquiry = () => {
    setEnquiryOpen(false);
    setEnqName("");
    setEnqEmail("");
    setEnqPhone("");
    setEnqPostcode("");
    setEnqMessage("");
    setEnqPrivacy(false);
    setEnqSuccess("");
    setEnqError("");
  };

  const handleEnquirySubmit = useCallback(async (e) => {
    e.preventDefault();
    setEnqError("");
    setEnqSuccess("");

    if (!enqEmail && !enqPhone) {
      setEnqError("Please provide at least one contact method — email or phone.");
      return;
    }

    if (!enqPrivacy) {
      setEnqError("Please agree to the Privacy Policy before submitting.");
      return;
    }

    if (!executeRecaptcha) {
      setEnqError("reCAPTCHA not ready. Please try again.");
      return;
    }

    setEnqLoading(true);
    try {
      const token = await executeRecaptcha('product_enquiry');
      await axios.post(`${API_BASE_URL}/api/enquiries/product`, {
        name:         enqName,
        email:        enqEmail,
        phone:        enqPhone,
        postcode:     enqPostcode,
        message:      enqMessage,
        product_id:   product?.id,
        product_name: product?.name,
        stock_number: product?.stock_number,
        recaptchaToken: token,
      });
      setEnqSuccess("Thanks — your enquiry has been sent. We'll be in touch shortly.");
      setEnqName("");
      setEnqEmail("");
      setEnqPhone("");
      setEnqPostcode("");
      setEnqMessage("");
      setEnqPrivacy(false);
    } catch (err) {
      setEnqError(err.response?.data?.error || "Failed to send enquiry. Please try again.");
    } finally {
      setEnqLoading(false);
    }
  }, [executeRecaptcha, enqName, enqEmail, enqPhone, enqPostcode, enqMessage, enqPrivacy, product]);

  if (!product) return <div className="product-loading">Loading...</div>;

  const files = product?.files || [];

  const rightSummaryRows = [
    { label: "Stock Number", value: product.stock_number },
    { label: "MPN",          value: product.mpn },
    { label: "Manufacturer", value: product.manufacturer },
    { label: "Hours",        value: product.hours },
    { label: "Year",         value: product.year },
    { label: "Condition",    value: product.condition },
    { label: "Quantity",     value: product.quantity > 1 ? product.quantity : null },
  ].filter((r) => isFilled(r.value));

  return (
      <div className="product-page1">

        {isAdminLoggedIn && (
            <button
                type="button"
                className="pd-admin-edit-btn"
                onClick={() => navigate(`/admin/products/edit/${product.id}`)}
                title="Edit this listing in the admin panel"
            >
              <i className="fas fa-pencil-alt" /> Edit Listing
            </button>
        )}

        {/* BREADCRUMB */}
        {breadcrumbs.length > 0 && (
            <nav className="pd-breadcrumb">
              <Link to="/" className="pd-bc-link">Home</Link>
              {breadcrumbs.map((crumb, i) => (
                  <React.Fragment key={crumb.id}>
                    <span className="pd-bc-sep">/</span>
                    {i === breadcrumbs.length - 1 ? (
                        <Link to={`/category/${crumb.id}`} className="pd-bc-link">{crumb.name}</Link>
                    ) : (
                        <Link to={`/category/${crumb.id}`} className="pd-bc-link">{crumb.name}</Link>
                    )}
                  </React.Fragment>
              ))}
              <span className="pd-bc-sep">/</span>
              <span className="pd-bc-current">{product.name}</span>
            </nav>
        )}

        <div className="product-container1">

          <div className="product-slider-container1">
            {files.length > 0 ? (
                <>
                  <Swiper
                      modules={[Navigation, Thumbs]}
                      navigation={files.length > 1}
                      thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
                      className="product-main-swiper"
                      onSlideChange={(swiper) => setLightboxIndex(swiper.activeIndex)}
                  >
                    {files.map((file, index) => (
                        <SwiperSlide key={index}>
                          <div className="product-image-container1" onClick={() => openLightbox(index)}>
                            <img src={file.url} alt={product.name} className="product-image1" />
                            <div className="product-image-zoom-hint">
                              <i className="fas fa-search-plus" />
                            </div>
                          </div>
                        </SwiperSlide>
                    ))}
                  </Swiper>

                  {files.length > 1 && (
                      <Swiper
                          modules={[FreeMode, Thumbs]}
                          onSwiper={setThumbsSwiper}
                          spaceBetween={8}
                          slidesPerView={4}
                          freeMode
                          watchSlidesProgress
                          className="product-thumb-swiper"
                      >
                        {files.map((file, index) => (
                            <SwiperSlide key={index}>
                              <div className="product-thumb-slide">
                                <img src={file.url} alt={`Thumbnail ${index + 1}`} className="product-thumb-img" />
                              </div>
                            </SwiperSlide>
                        ))}
                      </Swiper>
                  )}
                </>
            ) : (
                <div className="product-image-container1 no-image-wrap1">
                  <img src="/images/defaultimg.png" alt="Awaiting image" className="product-image1" />
                  <span className="no-image-label1">Awaiting Image</span>
                </div>
            )}

            {isFilled(product.description) && (
                <div className="product-description-box1">
                  <h3 className="product-description-title1">Description</h3>
                  <div className="product-description-text1">{product.description}</div>
                </div>
            )}
          </div>

          <div className="product-details1">
            <h1 className="product-title1">{product.name}</h1>

            {product.price_on_application ? (
                <div className="product-price-block1">
                  <div className="product-price-main1 product-poa">📞 Price on application</div>
                </div>
            ) : isFilled(product.price) ? (
                <div className="product-price-block1">
                  <div className="product-price-main1">{formatMoney(product.price)}</div>
                  {isFilled(product.price_inc_vat) && (
                      <div className="product-price-sub1">{formatMoney(product.price_inc_vat)} incl. VAT</div>
                  )}
                </div>
            ) : null}

            {rightSummaryRows.length > 0 && (
                <div className="product-spec-card1">
                  {rightSummaryRows.map((row) => (
                      <div className="product-spec-row1" key={row.label}>
                        <span className="product-spec-label1">{row.label}</span>
                        <span className="product-spec-value1">{row.value}</span>
                      </div>
                  ))}
                </div>
            )}

            <div className="product-actions1">
              <button className="product-primary-btn1" type="button" onClick={openEnquiry}>Make an Enquiry</button>
              <button className="product-secondary-btn1" type="button"><i className="fas fa-phone-alt" /> Call 07970 506155</button>
            </div>

            <div className="product-help-card1">
              <h3>Questions?</h3>
              <p>Contact us for more information about this item.</p>
              <div className="product-help-row1">
                <span>📞 07970 506155</span>
                <span>✉️ contact@ploughbreakers.co.uk</span>
              </div>
            </div>
          </div>
        </div>

        {lightboxOpen && (
            <div className="lightbox-overlay1" onClick={closeLightbox}>
              <div className="lightbox-content1" onClick={(e) => e.stopPropagation()}>
                <button className="lightbox-close-button1" onClick={closeLightbox}>
                  <i className="fas fa-times" />
                </button>
                <Swiper
                    modules={[Navigation]}
                    navigation={files.length > 1}
                    initialSlide={lightboxIndex}
                    onSwiper={(swiper) => { lightboxSwiperRef.current = swiper; }}
                    className="lightbox-swiper"
                >
                  {files.map((file, index) => (
                      <SwiperSlide key={index}>
                        <div className="lightbox-image-container1">
                          <img src={file.url} alt={product.name} className="lightbox-image1" />
                        </div>
                      </SwiperSlide>
                  ))}
                </Swiper>
              </div>
            </div>
        )}

        {enquiryOpen && (
            <div className="enq-overlay" onClick={closeEnquiry}>
              <div className="enq-modal" onClick={(e) => e.stopPropagation()}>
                <button className="enq-close" onClick={closeEnquiry}>
                  <i className="fas fa-times" />
                </button>

                <h2 className="enq-title">Make an Enquiry</h2>
                <p className="enq-product-name">{product.name}</p>
                {product.stock_number && (
                    <p className="enq-stock">Stock No: {product.stock_number}</p>
                )}

                {enqSuccess ? (
                    <div className="enq-success-msg">
                      <i className="fas fa-check-circle" /> {enqSuccess}
                      <button className="enq-close-btn" onClick={closeEnquiry}>Close</button>
                    </div>
                ) : (
                    <form className="enq-form" onSubmit={handleEnquirySubmit}>
                      {enqError && <p className="enq-error">{enqError}</p>}

                      <label>
                        Your name *
                        <input value={enqName} onChange={(e) => setEnqName(e.target.value)} required />
                      </label>

                      <div className="enq-form-row">
                        <label>
                          Email
                          <input
                              type="email"
                              value={enqEmail}
                              onChange={(e) => setEnqEmail(e.target.value)}
                              placeholder="your@email.com"
                          />
                        </label>
                        <label>
                          Phone
                          <input
                              type="tel"
                              value={enqPhone}
                              onChange={(e) => setEnqPhone(e.target.value)}
                              placeholder="07700 000000"
                          />
                        </label>
                      </div>
                      <p className="enq-form-hint">* At least one contact method required</p>

                      <label>
                        Postcode <span className="enq-hint-inline">(for delivery costs)</span>
                        <input
                            type="text"
                            value={enqPostcode}
                            onChange={(e) => setEnqPostcode(e.target.value)}
                            placeholder="DD8 5JZ"
                            maxLength={8}
                        />
                      </label>

                      <label>
                        Message *
                        <textarea
                            value={enqMessage}
                            onChange={(e) => setEnqMessage(e.target.value)}
                            rows={5}
                            required
                            placeholder="Tell us what you'd like to know about this item..."
                        />
                      </label>

                      <label className="enq-privacy-check">
                        <input
                            type="checkbox"
                            checked={enqPrivacy}
                            onChange={(e) => setEnqPrivacy(e.target.checked)}
                            required
                        />
                        <span>
                          I have read and agree to the{" "}
                          <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>
                        </span>
                      </label>

                      <button type="submit" className="enq-submit" disabled={enqLoading}>
                        {enqLoading ? "Sending..." : "Send Enquiry"}
                      </button>

                      <p className="enq-privacy-note">
                        By submitting this form you agree to our{" "}
                        <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>.
                      </p>
                    </form>
                )}
              </div>
            </div>
        )}
      </div>
  );
}

export default Product;